/**
 * Records a currency conversion from lines picked in the incoming queue: the
 * leg sold, the leg bought, and whatever else is selected as funding hops.
 * Nothing is typed - the backend reads amounts, currencies and date off the
 * lines.
 */

import { useState } from 'react';
import { ArrowLeftRight, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { FieldError } from '@/components/forms/FieldError';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  fundingHopsOf,
  hasAnyError,
  likeliestPairing,
  unbalancedHopTotals,
  validateConversionFromBankLines,
  type ConversionFromBankLinesFormState,
} from '@/components/bank-incoming/conversionFromBankLinesValidators';
import { useRecordConversionFromBankLines } from '@/hooks/react-query/useCurrencyConversions';
import type { BankIncomingAdminLine } from '@/types/bank-reconciliation';
import { formatCurrency, formatDate } from '@/utils/formatters';

const lineAmount = (line: BankIncomingAdminLine) =>
  formatCurrency(line.amount, { currency: line.currency ?? undefined });

const lineLabel = (line: BankIncomingAdminLine) =>
  `${formatDate(line.value_date ?? line.execution_date)} · ${line.account_name} · ${lineAmount(line)}`;

export function RecordConversionFromLinesPanel({
  fundId,
  lines,
  onRecorded,
  onCancel,
}: {
  fundId: string;
  lines: BankIncomingAdminLine[];
  onRecorded: () => void;
  onCancel: () => void;
}) {
  const [state, setState] = useState<ConversionFromBankLinesFormState>(
    () => likeliestPairing(lines) ?? { soldLineId: '', boughtLineId: '' }
  );
  const record = useRecordConversionFromBankLines(fundId);

  const errors = validateConversionFromBankLines(state, lines);
  const legsPicked = !!state.soldLineId && !!state.boughtLineId;
  const hops = fundingHopsOf(lines, state);
  const outgoing = lines.filter((line) => line.amount < 0);
  const incoming = lines.filter((line) => line.amount > 0);

  const submit = () =>
    record.mutate(
      {
        sold_line_id: state.soldLineId,
        bought_line_id: state.boughtLineId,
        funding_hop_ids: hops.map((hop) => hop.id),
      },
      { onSuccess: onRecorded }
    );

  return (
    <div className="space-y-4 rounded-md border border-border bg-muted/40 p-4">
      <div className="flex items-center gap-2">
        <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">
          Record currency conversion
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <LinePicker
          id="conversion-sold-line"
          label="Sold"
          placeholder="Line the sold currency left on"
          lines={outgoing}
          value={state.soldLineId}
          error={errors.soldLineId}
          onChange={(soldLineId) =>
            setState((current) => ({ ...current, soldLineId }))
          }
        />
        <LinePicker
          id="conversion-bought-line"
          label="Bought"
          placeholder="Line the bought currency arrived on"
          lines={incoming}
          value={state.boughtLineId}
          error={errors.boughtLineId}
          onChange={(boughtLineId) =>
            setState((current) => ({ ...current, boughtLineId }))
          }
        />
      </div>

      {!legsPicked && (
        <p className="text-sm text-muted-foreground">
          Pick the sold and bought lines; the rest of the selection are the
          funding hops.
        </p>
      )}
      {legsPicked && hops.length > 0 && <FundingHops hops={hops} />}
      {legsPicked && <FieldError message={errors.fundingHops} />}

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={hasAnyError(errors) || record.isPending}
          onClick={submit}
        >
          {record.isPending && (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          )}
          Record conversion
        </Button>
      </div>
    </div>
  );
}

function LinePicker({
  id,
  label,
  placeholder,
  lines,
  value,
  error,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  lines: BankIncomingAdminLine[];
  value: string;
  error: string | undefined;
  onChange: (lineId: string) => void;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {lines.map((line) => (
            <SelectItem key={line.id} value={line.id}>
              {lineLabel(line)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value && <FieldError message={error} />}
    </div>
  );
}

function FundingHops({ hops }: { hops: BankIncomingAdminLine[] }) {
  const unbalanced = unbalancedHopTotals(hops);

  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        Funding hops
      </p>
      {hops.map((hop) => (
        <div key={hop.id} className="flex justify-between text-sm">
          <span className="text-muted-foreground">
            {formatDate(hop.value_date ?? hop.execution_date)} ·{' '}
            {hop.account_name}
          </span>
          <span>{lineAmount(hop)}</span>
        </div>
      ))}
      <p
        className={
          unbalanced.size === 0
            ? 'text-xs text-muted-foreground'
            : 'text-xs text-amber-600'
        }
      >
        {unbalanced.size === 0
          ? 'Nets to zero'
          : `Left over: ${[...unbalanced]
              .map(([currency, amount]) =>
                formatCurrency(amount, {
                  currency: currency || undefined,
                })
              )
              .join(' · ')}`}
      </p>
    </div>
  );
}
